import { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const VEHICLE_TYPES = ['lorry','car','van','bike','jeep','cab','threewheel','other'];
const EMPTY = { type: '', number: '', insurance: '', owner: '', vehicle_reg: '', driver: '', driver_id_no: '' };

function VehicleModal({ initial, onClose, onSave }) {
  const [form, setForm]   = useState(initial || EMPTY);
  const [saving, setSaving] = useState(false);
  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const submit = async () => {
    if (!form.type) { toast.error('Vehicle type is required'); return; }
    setSaving(true);
    try { await onSave(form); onClose(); }
    catch (err) { toast.error(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal">
        <div className="modal-header">
          <h3>{initial?.id ? 'Edit Vehicle' : 'Add Vehicle'}</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Vehicle Type *</label>
              <select className="form-select" value={form.type} onChange={set('type')}>
                <option value="">— Select —</option>
                {VEHICLE_TYPES.map(t => (
                  <option key={t} value={t} style={{ textTransform: 'capitalize' }}>
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Vehicle Number</label>
              <input className="form-input" value={form.number} onChange={set('number')} placeholder="e.g. CAB 1234" />
            </div>
            <div className="form-group">
              <label className="form-label">Insurance</label>
              <input className="form-input" value={form.insurance} onChange={set('insurance')} placeholder="Insurance details" />
            </div>
            <div className="form-group">
              <label className="form-label">Owner</label>
              <input className="form-input" value={form.owner} onChange={set('owner')} placeholder="Owner name" />
            </div>
            <div className="form-group">
              <label className="form-label">Vehicle Registration</label>
              <input className="form-input" value={form.vehicle_reg} onChange={set('vehicle_reg')} placeholder="Registration no" />
            </div>
            <div className="form-group">
              <label className="form-label">Driver</label>
              <input className="form-input" value={form.driver} onChange={set('driver')} placeholder="Driver name" />
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Driver ID No</label>
              <input className="form-input" value={form.driver_id_no} onChange={set('driver_id_no')} placeholder="NIC / ID number" />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Vehicles({ member }) {
  const [rows, setRows]    = useState([]);
  const [loading, setLoad] = useState(false);
  const [modal, setModal]  = useState(null);
  const [deleting, setDel] = useState(null);

  useEffect(() => {
    if (!member?.id) return;
    setLoad(true);
    api.get(`/vehicles/${member.id}`)
      .then(r => setRows(r.data))
      .catch(() => toast.error('Failed to load vehicles'))
      .finally(() => setLoad(false));
  }, [member?.id]);

  const handleSave = async form => {
    if (modal.row?.id) {
      const { data } = await api.put(`/vehicles/${modal.row.id}`, form);
      setRows(p => p.map(r => r.id === data.id ? data : r));
      toast.success('Updated');
    } else {
      const { data } = await api.post('/vehicles', { member_id: member.id, ...form });
      setRows(p => [...p, data]);
      toast.success('Added');
    }
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this vehicle?')) return;
    setDel(id);
    try {
      await api.delete(`/vehicles/${id}`);
      setRows(p => p.filter(r => r.id !== id));
      toast.success('Deleted');
    } catch { toast.error('Delete failed'); }
    finally { setDel(null); }
  };

  if (!member?.id) return (
    <div className="empty-state"><p>Save Personal info first to manage vehicles.</p></div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className="btn btn-primary" onClick={() => setModal({ row: null })}>
          + Add Vehicle
        </button>
      </div>

      {loading ? (
        <p style={{ color: 'var(--text3)', textAlign: 'center', padding: 32 }}>Loading…</p>
      ) : rows.length === 0 ? (
        <div className="empty-state"><p>No vehicles added yet.</p></div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table>
            <thead>
              <tr>
                <th>Type</th>
                <th>Number</th>
                <th>Owner</th>
                <th>Driver</th>
                <th>Driver ID</th>
                <th style={{ width: 110 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.id}>
                  <td>
                    <span className="badge badge-yellow" style={{ textTransform: 'capitalize' }}>
                      {row.type}
                    </span>
                  </td>
                  <td>{row.number || '—'}</td>
                  <td>{row.owner || '—'}</td>
                  <td>{row.driver || '—'}</td>
                  <td>{row.driver_id_no || '—'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => setModal({ row })}>Edit</button>
                      <button className="btn btn-danger btn-sm" disabled={deleting === row.id}
                        onClick={() => handleDelete(row.id)}>
                        {deleting === row.id ? '…' : 'Del'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal !== null && (
        <VehicleModal initial={modal.row} onClose={() => setModal(null)} onSave={handleSave} />
      )}
    </div>
  );
}
